import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Calendar, BarChart3, ClipboardList, Users, FileText, FileCode, Key, UserCircle, Clock, Shuffle } from 'lucide-react';
import { getUserTimezone, formatTimezone } from '../utils/timezone';

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Get user's timezone
  const userTimezone = useMemo(() => getUserTimezone(), []);
  const timezoneDisplay = useMemo(() => formatTimezone(userTimezone), [userTimezone]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSyncAll = async () => {
    if (!user?.can_manage_users) return;
    
    setSyncing(true);
    try {
      const response = await api.post('/api/auth/sync-google-calendar/');
      const data = response.data;
      alert(`✅ Successfully synced all managers!\n${data.total_events || 0} events processed\n${data.total_created || 0} created\n${data.total_updated || 0} updated`);
    } catch (error: any) {
      console.error('Sync error:', error);
      alert(`❌ Failed to sync: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <div className="flex items-center gap-2">
                <a href="/">
                  <img 
                    src={process.env.NODE_ENV === 'production' ? '/static/images/logo.svg' : '/images/logo.svg'} 
                    alt="SharedEasy Logo" 
                    className="w-40 h-6"
                  />
                </a>
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link
                to="/calendar"
                className={`${
                  isActive('/calendar')
                    ? 'border-primary-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
              >
                <Calendar className="w-4 h-4" />
                Calendar
              </Link>
              
              {user?.role === 'manager' && (
                <Link
                  to="/hours"
                  className={`${
                    isActive('/hours')
                      ? 'border-primary-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                >
                  <Clock className="w-4 h-4" />
                  Hours
                </Link>
              )}
              
              {user?.can_view_all_tasks && (
                <>
                  <Link
                    to="/submissions"
                    className={`${
                      isActive('/submissions')
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                  >
                    <FileCode className="w-4 h-4" />
                    Submissions
                  </Link>
                  <Link
                    to="/clients"
                    className={`${
                      isActive('/clients')
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                  >
                    <UserCircle className="w-4 h-4" />
                    Clients
                  </Link>
                <Link
                  to="/tasks"
                  className={`${
                    isActive('/tasks')
                      ? 'border-primary-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                >
                  <ClipboardList className="w-4 h-4" />
                  Tasks
                </Link>
                  <Link
                    to="/dashboard"
                    className={`${
                      isActive('/dashboard')
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    Task Stats
                  </Link>
                  <Link
                    to="/queue-stats"
                    className={`${
                      isActive('/queue-stats')
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                  >
                    <Shuffle className="w-4 h-4" />
                    Queue Stats
                  </Link>
                  <Link
                    to="/users"
                    className={`${
                      isActive('/users')
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                  >
                    <Users className="w-4 h-4" />
                    Users
                  </Link>
                  {user?.can_manage_users && (
                    <Link
                      to="/api-tokens"
                      className={`${
                        isActive('/api-tokens')
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                    >
                      <Key className="w-4 h-4" />
                      API
                    </Link>
                  )}
                  {user?.can_view_all_tasks && (
                    <Link
                      to="/logs"
                      className={`${
                        isActive('/logs')
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center gap-1.5 px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
                    >
                      <FileText className="w-4 h-4" />
                      Logs
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            <div className="flex items-center space-x-4">
              {user?.can_manage_users && (
                <button
                  onClick={handleSyncAll}
                  disabled={syncing}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {syncing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>🔄 Sync All</>
                  )}
                </button>
              )}
              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  onBlur={() => setTimeout(() => setUserMenuOpen(false), 200)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  aria-label="User menu"
                >
                  <UserCircle className="w-6 h-6" />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {/* User name */}
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </p>
                      </div>

                      {/* Role */}
                      <div className="px-4 py-2 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Role:</span>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                            {user?.role}
                          </span>
                        </div>
                      </div>

                      {/* Timezone */}
                      <div className="px-4 py-2 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Timezone:
                          </span>
                          <span className="text-xs font-medium text-gray-700">
                            {timezoneDisplay}
                          </span>
                        </div>
                      </div>

                      {/* Logout */}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className="sm:hidden border-t border-gray-200">
        <div className="px-2 pt-2 pb-3 space-y-1">
          <Link
            to="/calendar"
            className={`${
              isActive('/calendar')
                ? 'bg-primary-50 border-primary-500 text-primary-700'
                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
            } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
          >
            <Calendar className="w-5 h-5" />
            Calendar
          </Link>
          {user?.role === 'manager' && (
            <Link
              to="/hours"
              className={`${
                isActive('/hours')
                  ? 'bg-primary-50 border-primary-500 text-primary-700'
                  : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
              } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
            >
              <Clock className="w-5 h-5" />
              Hours
            </Link>
          )}
          {user?.can_view_all_tasks && (
            <>
              <Link
                to="/dashboard"
                className={`${
                  isActive('/dashboard')
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
              >
                <BarChart3 className="w-5 h-5" />
                Task Stats
              </Link>
              <Link
                to="/queue-stats"
                className={`${
                  isActive('/queue-stats')
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
              >
                <Shuffle className="w-5 h-5" />
                Queue Stats
              </Link>
              <Link
                to="/tasks"
                className={`${
                  isActive('/tasks')
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
              >
                <ClipboardList className="w-5 h-5" />
                Tasks
              </Link>
              <Link
                to="/users"
                className={`${
                  isActive('/users')
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
              >
                <Users className="w-5 h-5" />
                Users
              </Link>
              <Link
                to="/clients"
                className={`${
                  isActive('/clients')
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
              >
                <UserCircle className="w-5 h-5" />
                Clients
              </Link>
              {user?.can_view_all_tasks && (
                <Link
                  to="/submissions"
                  className={`${
                    isActive('/submissions')
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                  } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
                >
                  <FileCode className="w-5 h-5" />
                  Submissions
                </Link>
              )}
              {user?.can_view_all_tasks && (
                <Link
                  to="/logs"
                  className={`${
                    isActive('/logs')
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                  } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
                >
                  <FileText className="w-5 h-5" />
                  Logs
                </Link>
              )}
              {user?.can_manage_users && (
                <Link
                  to="/api-tokens"
                  className={`${
                    isActive('/api-tokens')
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                  } flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors`}
                >
                  <Key className="w-5 h-5" />
                  API
                </Link>
              )}
            </>
          )}
        </div>
        {user?.can_manage_users && (
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={handleSyncAll}
              disabled={syncing}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {syncing ? (
                <>🔄 Syncing all managers...</>
              ) : (
                <>🔄 Sync All Google Calendars</>
              )}
            </button>
          </div>
        )}
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="flex items-center justify-center">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                aria-label="User menu"
              >
                <UserCircle className="w-8 h-8" />
              </button>

              {/* Dropdown menu for mobile */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {/* User name */}
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user?.email}
                      </p>
                    </div>

                    {/* Role */}
                    <div className="px-4 py-2 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Role:</span>
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                          {user?.role}
                        </span>
                      </div>
                    </div>

                    {/* Timezone */}
                    <div className="px-4 py-2 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Timezone:
                        </span>
                        <span className="text-xs font-medium text-gray-700">
                          {timezoneDisplay}
                        </span>
                      </div>
                    </div>

                    {/* Logout */}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;

